/**
 * js/import-drop-card.js — the "Start" dashboard card (October 2026, Simplify
 * plan row 2): New personal quote, New commercial quote, and the drop zone
 * below them. It replaced the Quick Actions grid, whose eight buttons only
 * navigated; these two start the quote. The rest of this header is about the
 * drop zone, which was this file's first job ("Import a Document").
 *
 * The third mount of window.AltechImport, and the one that changes the shape of
 * the job: before it, the only place in the whole app an agent could drop a dec
 * page was a small zone on step 0 of one tool, folded inside a collapsed
 * <details>. An agent who lands on the dashboard holding a PDF had to know the
 * quoter existed, open it, scroll, and expand a card. Now the file goes on the
 * screen they are already looking at.
 *
 * EAGER and its own module, for the reason js/client-intake-card.js documents:
 * js/dashboard-widgets.js is at its 1,800-line ceiling and is not edited for
 * this, so this file paints #widgetImportDrop ITSELF. A MutationObserver on
 * #dashboardView's style attribute stands in for the refreshAll() hook it
 * cannot have (goHome() and showDashboard() both clear that inline display).
 *
 * IT FETCHES NOTHING. Unlike the Client Forms card there is no state to poll,
 * so there is no TTL, no stand-down and no "never a measured zero" problem:
 * the tile renders the same thing every time and is correct before any network
 * exists. render() is therefore safe to call on every dashboard entry.
 *
 * EVERY PATH GOES THROUGH AltechImport, never App._routeDroppedFiles directly.
 * That is the whole point of the tile existing here rather than in the quoter:
 * the router writes through the quoting plugin's live DOM, and firing it from
 * the dashboard with that HTML never injected makes App._applyMerged write the
 * entire merge headlessly into App.data and toast a success. The door brings
 * the tool up first.
 *
 * It also owns the Cmd+K command, registered EAGERLY here for the reason
 * js/client-intake-card.js gives for its own: a command that only appears once
 * you have opened the tool it would have taken you to is not a command.
 */
window.ImportDropCard = (function () {
    'use strict';

    const CARD_ID = 'widgetImportDrop';
    const DASHBOARD_ID = 'dashboardView';

    let _observer = null;
    let _started = false;

    function esc(s) {
        if (window.Utils && typeof window.Utils.escapeHTML === 'function') return window.Utils.escapeHTML(s);
        return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
            { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
        ));
    }

    function iconHtml(name, size) {
        const src = window.DashboardIcons || window.DashboardWidgets;
        try {
            if (src && typeof src.icon === 'function') return src.icon(name, size || 16);
        } catch (_) { /* fall through to no glyph */ }
        return '';
    }

    /** Read at CALL time, so a failed load of the door degrades to a dead tile
     *  rather than a thrown render. */
    function door() {
        const d = window.AltechImport;
        return (d && typeof d.open === 'function') ? d : null;
    }

    /**
     * New personal quote: the Personal Intake, cleared through
     * App.startNewClient (its unsaved-work prompt, then step 0, where the line
     * is picked). Only once THIS navigation won: navigateTo resolves true only
     * for the call that opened its tool, so a second click elsewhere, or a
     * tool that failed to load, starts nothing. Resolves true when it started.
     */
    async function startPersonal() {
        const A = window.App;
        if (!A || typeof A.navigateTo !== 'function') return false;
        if ((await A.navigateTo('quoting')) !== true) return false;
        if (typeof A.startNewClient !== 'function') return false;
        return (await A.startNewClient()) !== false;
    }

    /** New commercial quote: the Commercial Intake, then CommercialQuoter.newQuote
     *  (it asks about an unsaved quote first and can be cancelled). */
    async function startCommercial() {
        const A = window.App;
        if (!A || typeof A.navigateTo !== 'function') return false;
        if ((await A.navigateTo('commercial')) !== true) return false;
        const Q = window.CommercialQuoter;
        if (!Q || typeof Q.newQuote !== 'function') return false;
        return (await Q.newQuote()) !== false;
    }

    function render() {
        if (typeof document === 'undefined') return false;
        const card = document.getElementById(CARD_ID);
        if (!card) return false;
        const head = `<div class="widget-header"><div class="widget-title">${iconHtml('plus', 16)} Start</div></div>`;
        // Each button names its own tool, so an agent without one sees the other.
        const starts = `<div class="idc-starts">
            <button type="button" class="idc-start" data-idc-start="personal" data-requires-tool="quoting">${iconHtml('user', 16)}<span>New personal quote</span></button>
            <button type="button" class="idc-start" data-idc-start="commercial" data-requires-tool="commercial">${iconHtml('briefcaseBusiness', 16)}<span>New commercial quote</span></button>
        </div>`;
        // role="button" rather than a real <button>: it is a drop target with
        // block children. The Enter/Space handler below completes the contract.
        const body = `<div class="idc-zone" id="idcZone" role="button" tabindex="0" data-requires-tool="quoting"
                aria-label="${esc('Import a policy document. Drop a file here, or press Enter to choose one.')}">
            <span class="idc-icon" aria-hidden="true">📄</span>
            <span class="idc-label">Drop a dec page, printout or XML</span>
            <span class="idc-hint">${esc('Or press Enter to choose a file. It opens in the Personal Intake.')}</span>
        </div>`;
        card.innerHTML = `${head}${starts}${body}`;
        return true;
    }

    /**
     * One delegated listener on the CARD, bound once. render() rewrites the
     * card's innerHTML, so a listener on the inner zone would be thrown away
     * every time. Guarded on the element rather than a module flag, the rule
     * for anything whose container can be re-injected.
     */
    function wire() {
        if (typeof document === 'undefined') return false;
        const card = document.getElementById(CARD_ID);
        if (!card) return false;
        if (card.dataset.idcWired) return true;
        card.dataset.idcWired = '1';

        const zone = () => card.querySelector('.idc-zone');

        card.addEventListener('dragover', (e) => {
            if (!e.target.closest || !e.target.closest('.idc-zone')) return;
            e.preventDefault();
            if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
            const z = zone();
            if (z) z.classList.add('drag-over');
        });
        card.addEventListener('dragleave', (e) => {
            if (!e.target.closest || !e.target.closest('.idc-zone')) return;
            const z = zone();
            if (z) z.classList.remove('drag-over');
        });
        card.addEventListener('drop', (e) => {
            if (!e.target.closest || !e.target.closest('.idc-zone')) return;
            // This preventDefault is also what tells the window-level handler in
            // js/data-backup.js that the drop was claimed, so it does not ask
            // again about a file that has already started importing.
            e.preventDefault();
            const z = zone();
            if (z) z.classList.remove('drag-over');
            const files = e.dataTransfer && e.dataTransfer.files;
            if (!files || !files.length) return;
            const d = door();
            if (d) void d.open(files);
        });
        card.addEventListener('click', (e) => {
            const start = e.target.closest && e.target.closest('[data-idc-start]');
            if (start) {
                const run = start.getAttribute('data-idc-start') === 'commercial' ? startCommercial : startPersonal;
                void run().catch((err) => console.warn('[ImportDropCard] start failed:', err && err.message));
                return;
            }
            if (!e.target.closest || !e.target.closest('.idc-zone')) return;
            const d = door();
            if (d && typeof d.pick === 'function') d.pick();
        });
        card.addEventListener('keydown', (e) => {
            if (!e.target.closest || !e.target.closest('.idc-zone')) return;
            if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
            e.preventDefault();
            const d = door();
            if (d && typeof d.pick === 'function') d.pick();
        });
        return true;
    }

    /**
     * The Cmd+K command. Feature-detected the way js/client-intake-card.js
     * registers its own, because the palette is a separate eager module and
     * this file must not depend on script order to exist.
     */
    function registerPalette() {
        const P = window.CommandPalette;
        if (!P || typeof P.register !== 'function') return false;
        P.register({
            id: 'import:document',
            tool: 'quoting',
            label: 'Import a document',
            hint: 'Action · dec page, HawkSoft printout or EZLynx XML into Personal Intake',
            icon: '📄',
            run: () => {
                const d = door();
                if (d && typeof d.pick === 'function') d.pick();
            },
        });
        P.register({
            id: 'action:new-commercial-quote',
            tool: 'commercial',
            label: 'New commercial quote',
            keywords: 'new quote',
            hint: 'Action · Commercial Intake, clear current and start fresh',
            icon: '💼',
            run: () => { void startCommercial().catch(() => {}); },
        });
        return true;
    }

    function dashboardVisible() {
        if (typeof document === 'undefined') return false;
        const dv = document.getElementById(DASHBOARD_ID);
        if (!dv) return false;
        if (dv.style && dv.style.display === 'none') return false;
        return !(typeof document.hidden === 'boolean' && document.hidden);
    }

    function tick() {
        render();
        wire();
    }

    function watchDashboard() {
        if (_observer || typeof MutationObserver !== 'function') return;
        const dv = document.getElementById(DASHBOARD_ID);
        if (!dv) return;
        _observer = new MutationObserver(() => { if (dashboardVisible()) tick(); });
        _observer.observe(dv, { attributes: true, attributeFilter: ['style'] });
    }

    function start() {
        if (_started || typeof document === 'undefined') return;
        _started = true;
        watchDashboard();
        registerPalette();
        // Paint AND wire now. The tile is static, so there is nothing to wait
        // for, and a tile that looks droppable before it is one teaches the
        // agent it does not work.
        render();
        wire();
        // ONE repaint, not a loop. js/dashboard-icons.js is twelve <script>
        // tags below this one, so the paint above had no glyph to read and
        // every load shipped the only bento header without one. Deferred
        // scripts all run before DOMContentLoaded; 'complete' means they have.
        if (document.readyState !== 'complete') {
            document.addEventListener('DOMContentLoaded', tick, { once: true });
        }
    }

    function stop() {
        if (_observer) { _observer.disconnect(); _observer = null; }
    }

    if (typeof document !== 'undefined' && document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start, { once: true });
    } else if (typeof document !== 'undefined') {
        start();
    }

    return {
        render, wire, registerPalette, start, stop, tick, dashboardVisible,
        startPersonal, startCommercial,
        CARD_ID, DASHBOARD_ID,
    };
})();
